/**
 * Cohort Chart Visualization
 * Creates multi-cohort comparison charts using Apache eCharts
 */

// Note: chartInstances is declared in absence-charts.js

/**
 * Generate color shades from a base color
 * Creates lighter and darker variations for the 5 absence categories
 *
 * @param {string} baseColor - Hex color code (e.g., '#0066cc')
 * @returns {array} Array of 5 RGB color strings
 */
function generateColorShades(baseColor) {
    // Convert hex to RGB
    const r = parseInt(baseColor.slice(1, 3), 16);
    const g = parseInt(baseColor.slice(3, 5), 16);
    const b = parseInt(baseColor.slice(5, 7), 16);

    // Generate shades with varying brightness
    // Satisfactory (lightest), At Risk, Moderate, Severe, Chronic Absence Rate (darkest)
    const factors = [1.4, 1.0, 0.8, 0.6, 0.45];

    return factors.map(factor => {
        const newR = Math.min(255, Math.round(r * factor));
        const newG = Math.min(255, Math.round(g * factor));
        const newB = Math.min(255, Math.round(b * factor));
        return `rgb(${newR}, ${newG}, ${newB})`;
    });
}

/**
 * Initialize cohort comparison chart
 *
 * @param {string} chartId - DOM element ID for the chart container
 * @param {object} cohortData - Data from Absence_Query::get_cohort_totals()
 * @returns {object} eCharts instance
 */
function initCohortComparisonChart(chartId, cohortData) {
    // Check if echarts is loaded
    if (typeof echarts === 'undefined') {
        //console.error('ECharts library not loaded!');
        return null;
    }

    const chartContainer = document.getElementById(chartId);
    if (!chartContainer) {
        //console.error('Chart container not found:', chartId);
        return null;
    }

    const chart = echarts.init(chartContainer);

    // Check current theme mode (uses variables from absence-charts.js)
    const isDarkMode = document.documentElement.getAttribute('data-bs-theme') === 'dark';
    const textColor = isDarkMode ? darkModeTextColor : lightModeTextColor;
    const axisLineColor = isDarkMode ? darkModeBorderColor : '#999';

    // Check if we have valid data
    if (!cohortData || !cohortData.cohorts || Object.keys(cohortData.cohorts).length === 0) {
        chart.setOption({
            title: {
                text: 'No Cohort Data Available',
                left: 'center',
                top: 'center',
                textStyle: {
                    fontSize: 16,
                    color: textColor
                }
            }
        });
        return chart;
    }

    // Find the first cohort that has weekly data to extract week labels
    const cohortsWithData = Object.values(cohortData.cohorts).filter(
        c => c.weekly_data && Object.keys(c.weekly_data).length > 0
    );

    if (cohortsWithData.length === 0) {
        chart.setOption({
            title: {
                text: 'No Weekly Data Available',
                left: 'center',
                top: 'center',
                textStyle: {
                    fontSize: 16,
                    color: textColor
                }
            }
        });
        return chart;
    }

    // Extract week labels for x-axis from the first cohort with data
    const firstCohort = cohortsWithData[0];
    const weeks = Object.keys(firstCohort.weekly_data).sort();
    const weekLabels = weeks.map(w => {
        const date = new Date(w + 'T00:00:00'); // Ensure correct parsing
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });

    // Generate series for each cohort
    const series = [];
    const cohortList = []; // Track cohort info for legend grouping
    const categories = [
        { key: 'satisfactory_pct', totalKey: 'satisfactory_total', name: 'Satisfactory', lineType: 'solid', width: 1 },
        { key: 'at_risk_pct', totalKey: 'at_risk_total', name: 'At Risk', lineType: 'solid', width: 1 },
        { key: 'moderate_pct', totalKey: 'moderate_total', name: 'Moderate', lineType: 'solid', width: 1 },
        { key: 'severe_pct', totalKey: 'severe_total', name: 'Severe', lineType: 'solid', width: 1 },
        { key: 'chronic_pct', totalKey: 'chronic_total', name: 'Chronic Absence Rate', lineType: 'solid', width: 1 }
    ];

    // Build series for each cohort (skip cohorts with no weekly data)
    Object.values(cohortData.cohorts).forEach(cohort => {
        if (!cohort.weekly_data || Object.keys(cohort.weekly_data).length === 0) {
            return; // Skip cohorts with no data
        }
        const shades = generateColorShades(cohort.color);
        const cohortSeriesNames = [];

        categories.forEach((category, idx) => {
            // Extract data for this category across all weeks (includes both pct and total)
            const data = weeks.map(week => {
                const weekData = cohort.weekly_data[week];
                if (!weekData) return null;

                // Calculate Chronic Absence Rate as sum of moderate + severe
                if (category.key === 'chronic_pct') {
                    const moderatePct = weekData['moderate_pct'] || 0;
                    const severePct = weekData['severe_pct'] || 0;
                    const moderateTotal = weekData['moderate_total'] || 0;
                    const severeTotal = weekData['severe_total'] || 0;
                    return {
                        value: parseFloat((moderatePct + severePct).toFixed(1)),
                        total: moderateTotal + severeTotal,
                        enrolled_total: weekData['enrolled_total'] || 0,
                        district_count: weekData['district_count'] || 0
                    };
                }

                return {
                    value: weekData[category.key],
                    total: weekData[category.totalKey] || 0,
                    enrolled_total: weekData['enrolled_total'] || 0,
                    district_count: weekData['district_count'] || 0
                };
            });

            const seriesName = `${cohort.name} - ${category.name}`;
            cohortSeriesNames.push(seriesName);

            series.push({
                name: seriesName,
                type: 'line',
                data: data,
                connectNulls: false,
                triggerLineEvent: true,
                lineStyle: {
                    width: category.width,
                    type: category.lineType
                },
                itemStyle: {
                    color: shades[idx]
                },
                symbolSize: 6,
                emphasis: {
                    focus: 'series',
                    blurScope: 'coordinateSystem',
                    scale: 1.3,
                    itemStyle: {
                        borderWidth: 2,
                        borderColor: shades[idx]
                    },
                    lineStyle: {
                        width: category.width + 2
                    }
                },
                // Store cohort info for tooltip grouping
                cohortName: cohort.name,
                categoryName: category.name
            });
        });

        // Store cohort info for legend generation
        cohortList.push({
            name: cohort.name,
            color: cohort.color,
            seriesNames: cohortSeriesNames
        });
    });

    // Build hidden legends to maintain series toggle state
    const allSeriesNames = [];
    const legendSelected = {};
    cohortList.forEach(cohort => {
        cohort.seriesNames.forEach(name => {
            allSeriesNames.push(name);
            // Only show Chronic Absence Rate series by default
            legendSelected[name] = name.indexOf('Chronic Absence Rate') !== -1;
        });
    });

    const legends = [{
        show: false,
        data: allSeriesNames,
        selected: legendSelected,
        selectedMode: true
    }];

    // No graphic labels needed — custom HTML legend handles cohort grouping
    const cohortLabels = [];

    // Chart configuration
    const option = {
        title: {
            text: 'Cohort Comparison by Week',
            left: 'center',
            textStyle: {
                fontSize: 18,
                fontWeight: 'bold',
                color: isDarkMode ? darkModeTextColor : lightModeTextColor,
            },
            top: 10
        },
        tooltip: {
            trigger: 'axis',
            backgroundColor: isDarkMode ? darkModeBGColor : lightModeBGColor,
            borderColor: isDarkMode ? darkModeBorderColor : lightModeBorderColor,
            textStyle: {
                color: textColor
            },
            axisPointer: {
                type: 'cross',
                label: {
                    backgroundColor: '#6a7985'
                }
            },
            formatter: function(params) {
                if (!params || params.length === 0) return '';

                let html = `<strong>${params[0].axisValueLabel}</strong><br/>`;

                // Group by cohort
                const cohorts = {};
                params.forEach(p => {
                    // Handle both object format {value, total} and simple number format
                    const dataValue = p.data;
                    if (dataValue === null || dataValue === undefined) return;

                    // Extract value and total from data object
                    let pctValue, totalValue;
                    if (typeof dataValue === 'object' && dataValue !== null) {
                        pctValue = dataValue.value;
                        totalValue = dataValue.total;
                    } else {
                        pctValue = dataValue;
                        totalValue = null;
                    }

                    if (pctValue === null || pctValue === undefined) return;

                    const cohortName = p.seriesName.split(' - ')[0];
                    const categoryName = p.seriesName.split(' - ')[1];

                    if (!cohorts[cohortName]) {
                        cohorts[cohortName] = { items: [], enrolled_total: 0, district_count: 0 };
                    }
                    // Capture enrollment and district count from the data object (same for all categories in a week)
                    if (typeof dataValue === 'object' && dataValue !== null) {
                        if (dataValue.enrolled_total) {
                            cohorts[cohortName].enrolled_total = dataValue.enrolled_total;
                        }
                        if (dataValue.district_count) {
                            cohorts[cohortName].district_count = dataValue.district_count;
                        }
                    }
                    cohorts[cohortName].items.push({
                        category: categoryName,
                        value: pctValue,
                        total: totalValue,
                        color: p.color
                    });
                });

                // Render grouped by cohort
                Object.entries(cohorts).forEach(([name, cohortGroup]) => {
                    html += `<br/><strong>${name}</strong><br/>`;
                    if (cohortGroup.district_count > 0) {
                        html += cohortGroup.district_count.toLocaleString() + (cohortGroup.district_count === 1 ? ' District' : ' Districts') + ' Reporting' + `<br/>`;
                    }
                    cohortGroup.items.forEach(item => {
                        html += `<span style="display:inline-block;width:10px;height:10px;background-color:${item.color};border-radius:50%;margin-right:5px;"></span>`;
                        html += `${item.category}: <strong>${item.value.toFixed(1)}%</strong>`;
                        html += `<br/>`;
                    });
                });

                return html;
            }
        },
        legend: legends,
        grid: {
            left: 37,
            right: 30,
            bottom: 28,
            top: 48,
            containLabel: true
        },
        xAxis: {
            type: 'category',
            name: 'Week Of',
            nameLocation: 'middle',
            nameGap: 54,
            nameTextStyle: {
                color: isDarkMode ? darkModeTextColor : lightModeTextColor
            },
            boundaryGap: false,
            data: weekLabels,
            axisLabel: {
                rotate: 45,
                fontSize: 11,
                color: isDarkMode ? darkModeTextColor : lightModeTextColor,
            },
            axisLine: {
                lineStyle: {
                    color: axisLineColor
                }
            }
        },
        yAxis: {
            type: 'value',
            name: 'Percentage',
            nameLocation: 'middle',
            nameGap: 42,
            nameTextStyle: {
                color: isDarkMode ? darkModeTextColor : lightModeTextColor
            },
            min: 0,
            max: 100,
            axisLabel: {
                formatter: '{value}%',
                fontSize: 11,
                color: isDarkMode ? darkModeTextColor : lightModeTextColor
            },
            splitLine: {
                lineStyle: {
                    type: 'dashed',
                    color: isDarkMode ? darkModeBorderColor : '#ddd'
                }
            },
            axisLine: {
                lineStyle: {
                    color: axisLineColor
                }
            }
        },
        series: series,
        toolbox: {
            feature: {
                myFullscreen: {
                    show: true,
                    title: 'Fullscreen',
                    icon: 'path://M4.5 11H3v4h4v-1.5H4.5V11zM3 7h1.5V4.5H7V3H3v4zm10.5 6.5H11V15h4v-4h-1.5v2.5zM11 3v1.5h2.5V7H15V3h-4z',
                    onclick: function() {
                        toggleChartFullscreen(chartContainer, chart);
                    }
                },
                restore: {},
                saveAsImage: {
                    title: 'Save as Image',
                    pixelRatio: 2
                }
            },
            right: 20,
            top: 10
        },
        graphic: cohortLabels
    };

    chart.setOption(option);

    // Build custom HTML legend with cohort grouping
    var legendWrapper = chartContainer.nextElementSibling;
    var legendTarget = legendWrapper ? legendWrapper.querySelector('.chart-legend-target') : null;
    if (legendTarget) {
        legendTarget.innerHTML = '';
        cohortList.forEach(function(cohort) {
            var shades = generateColorShades(cohort.color);
            var seriesConfig = cohort.seriesNames.map(function(name, idx) {
                return { name: name, color: shades[idx] };
            });

            var legendWrap = document.createElement('div');
            legendWrap.className = 'chart-legend-items';
            legendWrap.setAttribute('role', 'group');
            legendWrap.setAttribute('aria-label', cohort.name + ' legend');

            var heading = document.createElement('span');
            heading.className = 'chart-legend-cohort-label';
            heading.style.color = cohort.color;
            heading.textContent = cohort.name + ':';
            legendWrap.appendChild(heading);

            seriesConfig.forEach(function(item) {
                var btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'chart-legend-item';
                btn.setAttribute('data-series-name', item.name);

                // Apply initial inactive state if series is deselected
                if (legendSelected[item.name] === false) {
                    btn.classList.add('chart-legend-item--inactive');
                }

                var baseName = item.name.indexOf(' - ') !== -1 ? item.name.split(' - ')[1] : item.name;
                var definition = categoryDefinitions[baseName] || '';
                if (definition) {
                    btn.setAttribute('data-legend-tooltip', 'true');
                    btn.setAttribute('data-bs-placement', 'top');
                    btn.setAttribute('data-bs-custom-class', 'custom-tooltip');
                    btn.setAttribute('title', definition);
                }

                var swatch = document.createElement('span');
                swatch.className = 'chart-legend-swatch';
                swatch.style.backgroundColor = item.color;
                swatch.setAttribute('aria-hidden', 'true');

                var label = document.createElement('span');
                label.className = 'chart-legend-label';
                label.textContent = baseName;

                btn.appendChild(swatch);
                btn.appendChild(label);

                btn.addEventListener('click', function() {
                    var tip = bootstrap.Tooltip.getInstance(btn);
                    if (tip) {
                        tip.hide();
                    }
                    chart.dispatchAction({
                        type: 'legendToggleSelect',
                        name: item.name
                    });
                    btn.blur();
                });

                legendWrap.appendChild(btn);
            });

            legendTarget.appendChild(legendWrap);
        });

        // Sync toggle state using full series name
        chart.on('legendselectchanged', function(params) {
            var buttons = legendTarget.querySelectorAll('.chart-legend-item');
            buttons.forEach(function(btn) {
                var seriesName = btn.getAttribute('data-series-name');
                if (!seriesName) return;

                if (params.selected[seriesName] === false) {
                    btn.classList.add('chart-legend-item--inactive');
                } else {
                    btn.classList.remove('chart-legend-item--inactive');
                }
            });
        });

        // Initialize Bootstrap tooltips (hover only to avoid sticky tooltips on click)
        var tooltipTriggers = legendTarget.querySelectorAll('[data-legend-tooltip]');
        tooltipTriggers.forEach(function(el) {
            new bootstrap.Tooltip(el, { trigger: 'hover' });
        });
    }

    // Store cohort data with chart for theme updates
    chart._cohortList = cohortList;
    chart._cohortLabelPositions = [];

    // Store in global array for responsive handling
    if (typeof chartInstances !== 'undefined' && Array.isArray(chartInstances)) {
        chartInstances.push(chart);
    } else if (typeof chartInstances === 'undefined') {
        //console.warn('chartInstances array not found - creating it');
        window.chartInstances = [chart];
    }

    // Handle window resize
    window.addEventListener('resize', function() {
        chart.resize();
    });

    return chart;
}

/**
 * Update cohort chart graphic labels for theme changes
 * Called by the color mode observer in absence-charts.js
 * Note: With custom HTML legend, graphic labels are no longer used.
 * This function is kept for backward compatibility.
 */
function updateCohortChartGraphics(chart) {
    // No-op: cohort labels are now rendered as HTML in the custom legend
}

/**
 * Initialize all cohort charts on the page
 */
function initAllCohortCharts() {
    // Look for chart containers with data attributes
    document.querySelectorAll('[data-cohort-chart]').forEach(container => {
        const chartData = container.dataset.cohortData;

        if (chartData) {
            try {
                const data = JSON.parse(chartData);
                initCohortComparisonChart(container.id, data);
            } catch (e) {
                //console.error('Failed to parse cohort chart data:', e);
                //console.error('Raw data:', chartData);
            }
        } else {
            //console.warn('No chart data found for container:', container.id);
        }
    });
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
    // Check if echarts is loaded, otherwise wait for it
    if (typeof echarts !== 'undefined') {
        initAllCohortCharts();
    } else {
        // Retry after a short delay
        setTimeout(function() {
            if (typeof echarts !== 'undefined') {
                initAllCohortCharts();
            }
        }, 500);
    }
});
